import { ApiResponse, IDevice, IGateway, ITextLabel, AvailableResponse, SyncStatus, SyncResponse, IMap, MapResponse } from '../types';
import { UserSettings, ViewOptions, TooltipField, TriangulationSettings } from '../types/userSettings';
import { convertRelationalToPixelRadius } from '../utils/radiusUtils';

export interface AppSettings {
    endpoint: string;
    pollInterval: number;
    defaultImage: string;
    defaultZoom: number;
    minZoom: number;
    maxZoom: number;
}

// User settings types moved to separate file to avoid circular dependencies

export class ApiService {
    private baseUrl: string;
    private apiUrl: string;
    private token: string | null = null;

    constructor(baseUrl: string, apiUrl: string = process.env.REACT_APP_API_URL || 'http://localhost:3051/api') {
        this.baseUrl = baseUrl;
        this.apiUrl = apiUrl;
        this.token = localStorage.getItem('token');
    }

    setToken(token: string) {
        this.token = token;
        localStorage.setItem('token', token);
    }

    clearToken() {
        this.token = null;
        localStorage.removeItem('token');
    }

    private getHeaders(): HeadersInit {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };

        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }

        return headers;
    }

    async sendApiRequest(method: string, path: string, body?: any, errorMessage: string = 'Failed'): Promise<any> {
        // console.log("API Request:", method, path);
        const url = path.startsWith('http') ? path : `${this.apiUrl}${path}`;
        const response = await fetch(url, {
            method,
            headers: this.getHeaders(),
            ...(body && { body: JSON.stringify(body) }),
        });
        if (!response.ok) throw new Error(errorMessage);
        return response.json();
    }

    async login(email: string, password: string): Promise<{ token: string; user: { email: string; name?: string; slug?: string } }> {
        try {
            const data = await this.sendApiRequest('POST', 'https://server.switchq.com/api/user/signin', { email, password });

            if (data.status === 'success') {
                const token = data.data.token;

                // Set token immediately so subsequent requests include it in Authorization header
                this.setToken(token);

                // Fetch user data to get the slug
                let userSlug = null;
                try {
                    const userData = await this.sendApiRequest('GET', 'https://server.switchq.com/api/user');
                    userSlug = userData.data?.slug || userData.slug;
                    // console.log(`🔐 User slug obtained: ${userSlug}`);
                } catch (userError) {
                    // console.warn('Failed to fetch user data, slug will be null');
                }

                return {
                    token,
                    user: {
                        email: data.data.email,
                        name: `${data.data.first_name} ${data.data.last_name}`.trim(),
                        slug: userSlug
                    }
                };
            } else {
                throw new Error(data.data || 'Authentication failed');
            }
        } catch (error) {
            // console.error('Login failed:', error);
            throw new Error('Invalid credentials');
        }
    }

    async fetchDevices(): Promise<ApiResponse> {
        try {
            return await this.sendApiRequest('GET', this.baseUrl);
        } catch (error) {
            // console.error('API not available:', error);
            throw error;
        }
    }

    /**
     * DEPRECATED: Frontend should not process device positioning.
     * All positioning is now handled by the backend triangulation service.
     * This method is kept for backward compatibility but should not be used.
     */
    processDevicesData(apiData: ApiResponse, gateways: { [mac: string]: IGateway }, configuredDevices: IDevice[], existingDevices?: { [mac: string]: IDevice }, mapDimensions?: { width: number; height: number }): { [mac: string]: IDevice } {
        // console.warn('⚠️  processDevicesData is deprecated. Device positions should come from backend via API/SSE.');

        const processedDevices: { [mac: string]: IDevice } = {};

        Object.entries(apiData).forEach(([mac, deviceData]) => {
            const configuredDevice = configuredDevices.find(d => d.mac === mac);
            const existingDevice = existingDevices?.[mac];

            // NOTE: Position should come from backend, not calculated here
            processedDevices[mac] = {
                mac,
                name: configuredDevice?.name || mac,
                position: existingDevice?.position, // Use existing position from backend
                lastSeen: deviceData.received,
                strongestGateway: existingDevice?.strongestGateway,
                rssi: existingDevice?.rssi,
                // Preserve movement properties for smooth animation
                movementAngle: existingDevice?.movementAngle,
                targetPosition: existingDevice?.targetPosition,
                movementSpeed: existingDevice?.movementSpeed || 0.8
            };
        });

        return processedDevices;
    }

    // Gateway operations
    async fetchGateways(): Promise<IGateway[]> {
        return this.sendApiRequest('GET', '/gateways', undefined, 'Failed to fetch gateways');
    }

    async createGateway(gateway: Omit<IGateway, '_id' | 'createdAt' | 'updatedAt'>): Promise<IGateway> {
        return this.sendApiRequest('POST', '/gateways', gateway, 'Failed to create gateway');
    }

    async updateGateway(mac: string, updates: Partial<IGateway>): Promise<IGateway> {
        return this.sendApiRequest('PUT', `/gateways/${mac}`, updates, 'Failed to update gateway');
    }

    async deleteGateway(mac: string): Promise<void> {
        return this.sendApiRequest('DELETE', `/gateways/${mac}`, undefined, 'Failed to delete gateway');
    }

    // Device operations
    async fetchConfiguredDevices(): Promise<IDevice[]> {
        return this.sendApiRequest('GET', '/devices', undefined, 'Failed to fetch devices');
    }

    async createDevice(device: Omit<IDevice, '_id' | 'createdAt' | 'updatedAt'>): Promise<IDevice> {
        return this.sendApiRequest('POST', '/devices', device, 'Failed to create device');
    }

    async updateDevice(mac: string, updates: Partial<IDevice>): Promise<IDevice> {
        return this.sendApiRequest('PUT', `/devices/${mac}`, updates, 'Failed to update device');
    }

    async deleteDevice(mac: string): Promise<void> {
        return this.sendApiRequest('DELETE', `/devices/${mac}`, undefined, 'Failed to delete device');
    }

    // Settings operations
    async fetchSettings(): Promise<AppSettings> {
        const data = await this.sendApiRequest('GET', '/settings', undefined, 'Failed to fetch settings');
        return data.config;
    }

    async updateSettings(settings: AppSettings): Promise<AppSettings> {
        const data = await this.sendApiRequest('PUT', '/settings', { config: settings }, 'Failed to update settings');
        return data.config;
    }

    // Text label operations
    async fetchTextLabels(): Promise<ITextLabel[]> {
        return this.sendApiRequest('GET', '/labels', undefined, 'Failed to fetch labels');
    }

    async createTextLabel(label: Omit<ITextLabel, '_id' | 'createdAt' | 'updatedAt'>): Promise<ITextLabel> {
        return this.sendApiRequest('POST', '/labels', label, 'Failed to create label');
    }

    async updateTextLabel(id: string, updates: Partial<ITextLabel>): Promise<ITextLabel> {
        return this.sendApiRequest('PUT', `/labels/${id}`, updates, 'Failed to update label');
    }

    async deleteTextLabel(id: string): Promise<void> {
        return this.sendApiRequest('DELETE', `/labels/${id}`, undefined, 'Failed to delete label');
    }

    // Hybrid system operations
    async fetchAvailableDevices(): Promise<AvailableResponse<IDevice>> {
        return this.sendApiRequest('GET', '/available/devices', undefined, 'Failed to fetch available devices');
    }

    async fetchAvailableGateways(lastSeenDays?: number | null): Promise<AvailableResponse<IGateway>> {
        const url = lastSeenDays ? `/available/gateways?lastSeenDays=${lastSeenDays}` : '/available/gateways';
        return this.sendApiRequest('GET', url, undefined, 'Failed to fetch available gateways');
    }

    async fetchMonitoredDevices(options?: { freshSignals?: boolean }): Promise<AvailableResponse<IDevice>> {
        const queryParam = options?.freshSignals ? '?freshSignals=true' : '';
        return this.sendApiRequest('GET', `/devices/monitored${queryParam}`, undefined, 'Failed to fetch monitored devices');
    }

    async fetchMonitoredGateways(): Promise<AvailableResponse<IGateway>> {
        return this.sendApiRequest('GET', '/gateways/monitored', undefined, 'Failed to fetch monitored gateways');
    }

    async addDeviceToMonitoring(mac: string, settings: { displayName?: string; color?: string; icon?: string; showTrail?: boolean }): Promise<{ success: boolean; device: IDevice; message: string }> {
        return this.sendApiRequest('POST', `/devices/monitor/${mac}`, settings, 'Failed to add device to monitoring');
    }

    async removeDeviceFromMonitoring(mac: string): Promise<{ success: boolean; device: IDevice; message: string }> {
        return this.sendApiRequest('DELETE', `/devices/monitor/${mac}`, undefined, 'Failed to remove device from monitoring');
    }

    async addGatewayToMonitoring(
        mac: string,
        position: { x: number; y: number },
        radius: number,
        settings?: { displayName?: string; color?: string; showCoverage?: boolean; logicThreshold?: number; maxRSSI?: number },
        mapId?: string
    ): Promise<{ success: boolean; gateway: IGateway; message: string }> {
        return this.sendApiRequest('POST', `/gateways/monitor/${mac}`, { position, radius, mapId, ...settings }, 'Failed to add gateway to monitoring');
    }

    async removeGatewayFromMonitoring(mac: string): Promise<{ success: boolean; gateway: IGateway; message: string }> {
        return this.sendApiRequest('DELETE', `/gateways/monitor/${mac}`, undefined, 'Failed to remove gateway from monitoring');
    }

    async triggerSync(): Promise<SyncResponse> {
        return this.sendApiRequest('POST', '/available/sync', undefined, 'Failed to trigger sync');
    }

    async getSyncStatus(): Promise<{ success: boolean; status: SyncStatus }> {
        return this.sendApiRequest('GET', '/available/status', undefined, 'Failed to get sync status');
    }

    async startPeriodicSync(): Promise<{ success: boolean; status: SyncStatus; message: string }> {
        return this.sendApiRequest('POST', '/available/sync/start', undefined, 'Failed to start periodic sync');
    }

    async stopPeriodicSync(): Promise<{ success: boolean; status: SyncStatus; message: string }> {
        return this.sendApiRequest('POST', '/available/sync/stop', undefined, 'Failed to stop periodic sync');
    }

    async updateGatewaySettings(
        mac: string,
        settings: { viewRadius?: number; logicThreshold?: number; mapId?: string }
    ): Promise<{ success: boolean; gateway: IGateway; message: string; updatedFields: any }> {
        return this.sendApiRequest('PATCH', `/gateways/${mac}/settings`, settings, 'Failed to update gateway settings');
    }

    // =============================================================================
    // MAP MANAGEMENT METHODS
    // =============================================================================

    async fetchMaps(): Promise<MapResponse> {
        return this.sendApiRequest('GET', '/maps', undefined, 'Failed to fetch maps');
    }

    async fetchSharedMaps(): Promise<MapResponse> {
        return this.sendApiRequest('GET', '/maps/shared', undefined, 'Failed to fetch shared maps');
    }

    async fetchPublicMaps(limit: number = 20, skip: number = 0): Promise<MapResponse> {
        return this.sendApiRequest('GET', `/maps/public?limit=${limit}&skip=${skip}`, undefined, 'Failed to fetch public maps');
    }

    async fetchMap(mapId: string): Promise<MapResponse> {
        return this.sendApiRequest('GET', `/maps/${mapId}`, undefined, 'Failed to fetch map');
    }

    async createMap(map: {
        name: string;
        description?: string;
        slug?: string;
        settings?: Partial<IMap['settings']>;
        metadata?: Partial<IMap['metadata']>;
        isPublic?: boolean;
    }): Promise<MapResponse> {
        return this.sendApiRequest('POST', '/maps', map, 'Failed to create map');
    }

    async updateMap(mapId: string, updates: {
        name?: string;
        description?: string;
        settings?: Partial<IMap['settings']>;
        metadata?: Partial<IMap['metadata']>;
        isPublic?: boolean;
    }): Promise<MapResponse> {
        return this.sendApiRequest('PUT', `/maps/${mapId}`, updates, 'Failed to update map');
    }

    async deleteMap(mapId: string): Promise<MapResponse> {
        return this.sendApiRequest('DELETE', `/maps/${mapId}`, undefined, 'Failed to delete map');
    }

    async duplicateMap(mapId: string): Promise<MapResponse> {
        return this.sendApiRequest('POST', `/maps/${mapId}/duplicate`, undefined, 'Failed to duplicate map');
    }

    // Map collaboration methods
    async getMapCollaborators(mapId: string): Promise<{
        success: boolean;
        collaborators: IMap['collaborators'];
        count: number;
    }> {
        return this.sendApiRequest('GET', `/maps/${mapId}/collaborators`, undefined, 'Failed to fetch collaborators');
    }

    async addMapCollaborator(mapId: string, userId: string, role: 'editor' | 'viewer'): Promise<{
        success: boolean;
        message: string;
        collaborators: IMap['collaborators'];
    }> {
        return this.sendApiRequest('POST', `/maps/${mapId}/collaborators`, { userId, role }, 'Failed to add collaborator');
    }

    async updateMapCollaborator(mapId: string, userId: string, role: 'editor' | 'viewer'): Promise<{
        success: boolean;
        message: string;
        collaborators: IMap['collaborators'];
    }> {
        return this.sendApiRequest('PUT', `/maps/${mapId}/collaborators/${userId}`, { role }, 'Failed to update collaborator');
    }

    async removeMapCollaborator(mapId: string, userId: string): Promise<{
        success: boolean;
        message: string;
        collaborators: IMap['collaborators'];
    }> {
        return this.sendApiRequest('DELETE', `/maps/${mapId}/collaborators/${userId}`, undefined, 'Failed to remove collaborator');
    }

    // Map-specific gateway methods
    async fetchMapGateways(mapId: string): Promise<{
        success: boolean;
        count: number;
        gateways: IGateway[];
    }> {
        return this.sendApiRequest('GET', `/gateways/map/${mapId}`, undefined, 'Failed to fetch map gateways');
    }

    async addGatewayToMap(mapId: string, gateway: {
        mac: string;
        name: string;
        position: { x: number; y: number };
        radius: number;
        customSettings?: IGateway['customSettings'];
    }): Promise<{
        success: boolean;
        message: string;
        gateway: IGateway;
    }> {
        return this.sendApiRequest('POST', `/gateways/map/${mapId}`, gateway, 'Failed to add gateway to map');
    }

    async updateMapGateway(mapId: string, gatewayId: string, updates: Partial<IGateway>): Promise<{
        success: boolean;
        message: string;
        gateway: IGateway;
    }> {
        return this.sendApiRequest('PUT', `/gateways/map/${mapId}/${gatewayId}`, updates, 'Failed to update map gateway');
    }

    async removeGatewayFromMap(mapId: string, gatewayId: string): Promise<{
        success: boolean;
        message: string;
    }> {
        return this.sendApiRequest('DELETE', `/gateways/map/${mapId}/${gatewayId}`, undefined, 'Failed to remove gateway from map');
    }

    async fetchAllUserGateways(): Promise<{
        success: boolean;
        totalCount: number;
        gatewaysByMap: { [mapId: string]: { map: IMap; gateways: IGateway[] } };
        allGateways: IGateway[];
    }> {
        return this.sendApiRequest('GET', '/gateways/user/all', undefined, 'Failed to fetch user gateways');
    }

    async saveMapPreset(mapId: string, center: [number, number], zoom: number): Promise<{
        success: boolean;
        message: string;
        savedView: { center: { x: number; y: number }; zoom: number };
    }> {
        return this.sendApiRequest('PATCH', `/maps/${mapId}/preset`, { center, zoom }, 'Failed to save map preset');
    }

    // =============================================================================
    // USER SETTINGS METHODS
    // =============================================================================

    async fetchUserSettings(): Promise<UserSettings> {
        return this.sendApiRequest('GET', '/user-settings', undefined, 'Failed to fetch user settings');
    }

    async updateUserSettings(settings: { viewOptions?: ViewOptions; tooltipFields?: TooltipField[]; triangulation?: TriangulationSettings }): Promise<UserSettings> {
        return this.sendApiRequest('PUT', '/user-settings', settings, 'Failed to update user settings');
    }

    async updateViewOption(key: keyof ViewOptions, value: boolean): Promise<UserSettings> {
        return this.sendApiRequest('PATCH', '/user-settings/view-option', { key, value }, 'Failed to update view option');
    }

    async updateTooltipFields(tooltipFields: TooltipField[]): Promise<UserSettings> {
        return this.sendApiRequest('PATCH', '/user-settings/tooltip-fields', { tooltipFields }, 'Failed to update tooltip fields');
    }
}
